import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { LuCheck } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

/**
 * Standard Switch Toggle Component
 * Clean, flat pill toggle switch consistent with ManageUsers / ManageCompanies.
 */
export const StandardSwitch = ({
  checked = false,
  onChange,
  disabled = false,
  activeColor = "#659d33",
  isDark = false,
}) => {
  const tok = getOrgTokens(isDark);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  return (
    <Box
      onClick={handleClick}
      role="switch"
      aria-checked={checked}
      sx={{
        position: "relative",
        width: 40,
        height: 22,
        borderRadius: "11px",
        bgcolor: checked ? activeColor : (isDark ? "rgba(255, 255, 255, 0.16)" : "rgba(0, 0, 0, 0.16)"),
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s ease-in-out",
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "3px",
          left: checked ? "calc(100% - 19px)" : "3px",
          width: 16,
          height: 16,
          borderRadius: "50%",
          bgcolor: "#ffffff",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
        }}
      >
        {checked && <LuCheck size={10} color={activeColor} strokeWidth={3} />}
      </Box>
    </Box>
  );
};

/**
 * Standard Toggle Card / Row Component
 * Reusable full row item with icon, label, and standard switch toggle.
 */
export const StandardToggleRow = ({
  icon: Icon,
  label,
  checked = false,
  onChange,
  disabled = false,
  activeColor = "#659d33",
  isDark = false,
  sx = {},
}) => {
  const { t } = useTranslation();
  const tok = getOrgTokens(isDark);

  return (
    <Box
      onClick={() => !disabled && onChange && onChange(!checked)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.25,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: checked
          ? alpha(activeColor, isDark ? 0.4 : 0.3)
          : tok.border.table,
        bgcolor: checked
          ? alpha(activeColor, isDark ? 0.08 : 0.05)
          : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: checked
            ? alpha(activeColor, isDark ? 0.12 : 0.08)
            : (isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)"),
          borderColor: checked ? activeColor : tok.border.card,
        },
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, textAlign: "left" }}>
        {Icon && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: checked ? activeColor : (isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)"),
              color: checked ? "#ffffff" : (isDark ? "#94a3b8" : "#64748b"),
              transition: "all 0.2s ease",
            }}
          >
            <Icon size={15} />
          </Box>
        )}
        <Typography
          sx={{
            fontFamily: ORG_PALETTE.font.family,
            fontSize: "13px",
            fontWeight: ORG_PALETTE.font.weight.medium,
            color: checked ? (isDark ? "#f1f5f9" : "#0f172a") : (isDark ? "#cbd5e1" : "#334155"),
            textAlign: "left",
          }}
        >
          {t(label)}
        </Typography>
      </Box>

      <StandardSwitch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        activeColor={activeColor}
        isDark={isDark}
      />
    </Box>
  );
};

export default StandardToggleRow;
